import { defineComponent, ref, computed, openBlock, createElementBlock, normalizeClass, toDisplayString, createCommentVNode, createElementVNode, normalizeStyle, Fragment, createTextVNode } from 'vue';
import { useElementSize } from '@vueuse/core';

var LoaderVariant;
(function (LoaderVariant) {
  LoaderVariant["Circular"] = "circular";
  LoaderVariant["Linear"] = "linear";
  LoaderVariant["Text"] = "text";
})(LoaderVariant || (LoaderVariant = {}));
var LoaderColor;
(function (LoaderColor) {
  LoaderColor["Primary"] = "primary";
  LoaderColor["Danger"] = "danger";
  LoaderColor["Inherit"] = "inherit";
})(LoaderColor || (LoaderColor = {}));
var LoaderSize;
(function (LoaderSize) {
  LoaderSize["M"] = "m";
  LoaderSize["S"] = "s";
})(LoaderSize || (LoaderSize = {}));

const LoaderRoot = [
// Layout
'flex', 'flex-col', 'gap-md'];
const LoaderRootColors = {
  'primary': [
  // Theme
  'stroke-primary', 'dark:stroke-primary-dark'],
  'danger': [
  // Theme
  'stroke-error', 'dark:stroke-error-dark'],
  'inherit': []
};
const LoaderContainer = ['flex', 'justify-center'];
const LoaderTitle = ['typography-headline-2', 'text-center', 'text-on-background', 'dark:text-on-background-dark'];
const LoaderDescription = ['typography-body-2-regular', 'text-center', 'text-outline', 'dark:text-outline-dark'];

var script = defineComponent({
  name: 'OrLoader',
  props: {
    variant: {
      type: String,
      default: () => LoaderVariant.Circular
    },
    /**
     * Defines a semantic color of the loading indicator.
     * Use `'inherit'` option to set your own color from the parent (not recommended).
     */
    color: {
      type: String,
      default: () => LoaderColor.Primary
    },
    /**
     * Defines a diameter of the circular loader. Ignored for linear one.
     */
    size: {
      type: String,
      default: () => LoaderSize.M
    },
    title: {
      type: String,
      default: undefined
    },
    description: {
      type: String,
      default: undefined
    },
    text: {
      type: String,
      default: 'Loading'
    }
  },
  expose: ['root'],
  setup(props) {
    // Refs
    const root = ref();
    const {
      width: loaderContainerWidth
    } = useElementSize(root);
    // Styles
    const rootStyles = computed(() => ['or-loader-v3', ...LoaderRoot, ...LoaderRootColors[props.color]]);
    const titleStyles = computed(() => [...LoaderTitle]);
    const descriptionStyles = computed(() => [...LoaderDescription]);
    const containerStyles = computed(() => [...LoaderContainer]);
    // State
    // eslint-disable-next-line vue/return-in-computed-property
    const computedSize = computed(() => {
      switch (props.size) {
        case LoaderSize.M:
          return 20;
        case LoaderSize.S:
          return 16;
      }
    });
    const strokeWidth = computed(() => {
      return loaderContainerWidth.value ? loaderContainerWidth.value - 2 : '100%';
    });
    /**
     * Preserves circular loading indicator from shrinking caused by external styles.
     */
    const wrapperStyle = computed(() => {
      return `min-width: ${computedSize.value}px;`;
    });
    return {
      root,
      rootStyles,
      containerStyles,
      titleStyles,
      descriptionStyles,
      computedSize,
      strokeWidth,
      wrapperStyle
    };
  }
});

const _hoisted_1 = ["width", "height"];
const _hoisted_2 = {
  fill: "none",
  "stroke-width": "2",
  "stroke-linecap": "round"
};
const _hoisted_3 = ["stroke"];
const _hoisted_4 = {
  key: 1,
  width: "100%",
  height: "4"
};
const _hoisted_5 = {
  fill: "none",
  "stroke-width": "4",
  "stroke-linecap": "round"
};
const _hoisted_6 = ["x2"];
const _hoisted_7 = ["x2", "stroke"];
const _hoisted_8 = /*#__PURE__*/createElementVNode("span", {
  class: "animate-pulse"
}, ".", -1 /* HOISTED */);
const _hoisted_9 = /*#__PURE__*/createElementVNode("span", {
  class: "animate-pulse animation-delay-200"
}, ".", -1 /* HOISTED */);
const _hoisted_10 = /*#__PURE__*/createElementVNode("span", {
  class: "animate-pulse animation-delay-400"
}, ".", -1 /* HOISTED */);
function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [_ctx.title ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass(_ctx.titleStyles)
  }, toDisplayString(_ctx.title), 3 /* TEXT, CLASS */)) : createCommentVNode("v-if", true), createElementVNode("div", {
    class: normalizeClass(_ctx.containerStyles),
    style: normalizeStyle(_ctx.wrapperStyle)
  }, [_ctx.variant === 'circular' ? (openBlock(), createElementBlock("svg", {
    key: 0,
    viewBox: "0 0 16 16",
    width: _ctx.computedSize,
    height: _ctx.computedSize
  }, [createElementVNode("g", _hoisted_2, [createElementVNode("circle", {
    class: normalizeClass(['animate-circular-loader', 'origin-center']),
    cx: "50%",
    cy: "50%",
    r: "7",
    stroke: _ctx.color === 'inherit' ? 'currentColor' : 'inherit'
  }, null, 8 /* PROPS */, _hoisted_3)])], 8 /* PROPS */, _hoisted_1)) : createCommentVNode("v-if", true), _ctx.variant === 'linear' ? (openBlock(), createElementBlock("svg", _hoisted_4, [createElementVNode("g", _hoisted_5, [createElementVNode("line", {
    class: normalizeClass(['stroke-surface-variant', 'dark:stroke-surface-variant-dark']),
    x1: "2",
    y1: "50%",
    x2: _ctx.strokeWidth,
    y2: "50%"
  }, null, 8 /* PROPS */, _hoisted_6), createElementVNode("line", {
    class: normalizeClass(['animate-linear-loader', 'origin-center']),
    x1: "2",
    y1: "50%",
    x2: _ctx.strokeWidth,
    y2: "50%",
    stroke: _ctx.color === 'inherit' ? 'currentColor' : 'inherit'
  }, null, 8 /* PROPS */, _hoisted_7)])])) : createCommentVNode("v-if", true), _ctx.variant === 'text' ? (openBlock(), createElementBlock(Fragment, {
    key: 2
  }, [createTextVNode(toDisplayString(_ctx.text) + " ", 1 /* TEXT */), _hoisted_8, _hoisted_9, _hoisted_10], 64 /* STABLE_FRAGMENT */)) : createCommentVNode("v-if", true)], 6 /* CLASS, STYLE */), _ctx.description ? (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass(_ctx.descriptionStyles)
  }, toDisplayString(_ctx.description), 3 /* TEXT, CLASS */)) : createCommentVNode("v-if", true)], 2 /* CLASS */);
}

script.render = render;
script.__file = "src/components/or-loader-v3/OrLoader.vue";

export { LoaderColor, LoaderSize, LoaderVariant, script as OrLoaderV3 };
